import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { aboutAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { Target, Eye, Heart, History, CheckCircle, ArrowRight } from 'lucide-react';

const AboutContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
  flex-direction: column;
`;

const HeroSlider = styled.section`
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 400px;
  }
`;

const SlideContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.currentSlide * 100}%);
`;

const Slide = styled.div`
  position: relative;
  min-width: 100%;
  height: 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
  }
`;

const SlideContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
  padding: 2rem;
  max-width: 800px;
`;

const SlideTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  
  @media (max-width: 768px) {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
  }
`;

const SliderNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 2rem;' : 'right: 2rem;'}
  z-index: 2;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  color: ${props => props.theme.colors.primary};
  
  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${props => props.direction === 'left' ? 'left: 1rem;' : 'right: 1rem;'}
  }
`;

const SliderDots = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  gap: 0.5rem;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  background: ${props => props.active ? 'white' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: white;
  }
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: ${props => props.theme.colors.white};
  padding: 6rem 2rem 4rem 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  margin: 0 0 1rem 0;
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  margin: 0;
  opacity: 0.9;
`;

const Section = styled.section`
  flex: 1;
  width: 100%;
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
  text-align: center;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.primary};
    margin: 1rem auto;
    border-radius: 2px;
  }
`;

const Description = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  line-height: 1.8;
  color: ${props => props.theme.colors.text.secondary};
  text-align: justify;
  max-width: 900px;
  margin: 0 auto 3rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const InfoCard = styled(Card)`
  padding: 2rem;
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  background: ${props => props.color}20;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: ${props => props.color};
`;

const CardTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const CardContent = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  line-height: 1.6;
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  text-align: justify;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ServiceCard = styled(Card)`
  padding: 2rem;
  text-align: center;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ServiceTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const ServiceDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  line-height: 1.6;
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
  text-align: justify;
`;

const ObjectivesList = styled.div`
  max-width: 800px;
  margin: 3rem auto;
`;

const ObjectiveItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
  box-shadow: ${props => props.theme.shadows.sm};
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(10px);
  }
`;

const ObjectiveIcon = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.25rem;
`;

const ObjectiveText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  line-height: 1.6;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const ImageSection = styled.div`
  margin: 3rem 0;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.lg};
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const HistorySection = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 3rem;
  box-shadow: ${props => props.theme.shadows.md};
`;

const HistoryText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.base};
  line-height: 1.8;
  color: ${props => props.theme.colors.text.secondary};
  white-space: pre-wrap;
  text-align: justify;
`;

const BoardSection = styled.section`
  flex: 1;
  width: 100%;
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const BoardCard = styled(Card)`
  padding: 1.75rem 1.5rem;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const BoardAvatar = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  margin: 0 auto 1rem;
  overflow: hidden;
  background-color: ${props => props.theme.colors.primary}15;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  color: ${props => props.theme.colors.primary};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BoardName = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.25rem 0;
`;

const BoardPosition = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
`;

const BoardBio = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0.75rem 0 0 0;
  line-height: 1.6;
  text-align: left;
`;

const CTASection = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: ${props => props.theme.colors.primary}10;
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-top: 3rem;
`;

const CTATitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const CTAText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 2rem 0;
`;

const AboutPage = () => {
  const { theme } = useTheme();
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      const response = await aboutAPI.get();
      setAboutData(response.data.data);
    } catch (error) {
      console.error('Error fetching about data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AboutContainer theme={theme}>
        <PublicHeader theme={theme} />
        <Section>
          <div style={{ textAlign: 'center', padding: '3rem', fontSize: '1.2rem', color: theme.colors.text.secondary }}>
            Loading...
          </div>
        </Section>
        <PublicFooter theme={theme} />
      </AboutContainer>
    );
  }

  if (!aboutData) {
    return (
      <AboutContainer theme={theme}>
        <PublicHeader theme={theme} />
        <Section>
          <div style={{ textAlign: 'center', padding: '3rem', fontSize: '1.2rem', color: theme.colors.text.secondary }}>
            No content available.
          </div>
        </Section>
        <PublicFooter theme={theme} />
      </AboutContainer>
    );
  }

  return (
    <AboutContainer theme={theme}>
      <PublicHeader theme={theme} />
      
      <HeroSection theme={theme}>
        <HeroTitle>{aboutData.title || 'About Us'}</HeroTitle>
        <HeroSubtitle>Learn more about our organization and what we do</HeroSubtitle>
      </HeroSection>

      {/* Main Description */}
      <Section>
        {aboutData.description && (
          <Description theme={theme}>{aboutData.description}</Description>
        )}

        {aboutData.what_we_do && (
          <div style={{ marginTop: '3rem' }}>
            <SectionTitle theme={theme}>What We Do</SectionTitle>
            <Description theme={theme}>{aboutData.what_we_do}</Description>
          </div>
        )}

        {aboutData.image_url && (
          <ImageSection theme={theme}>
            <img 
              src={`${process.env.REACT_APP_API_URL.replace('/api', '')}/storage/${aboutData.image_url}`} 
              alt={aboutData.title}
              onError={(e) => {
                e.target.style.display = 'none';
                console.error('Failed to load image:', e.target.src);
              }}
            />
          </ImageSection>
        )}
      </Section>

      {/* Mission, Vision, Values */}
      {(aboutData.mission || aboutData.vision || aboutData.values) && (
        <Section style={{ background: theme.colors.background.secondary }}>
          <Grid>
            {aboutData.mission && (
              <InfoCard theme={theme}>
                <IconWrapper color={theme.colors.primary}>
                  <Target size={32} />
                </IconWrapper>
                <CardTitle theme={theme}>Our Mission</CardTitle>
                <CardContent theme={theme}>{aboutData.mission}</CardContent>
              </InfoCard>
            )}

            {aboutData.vision && (
              <InfoCard theme={theme}>
                <IconWrapper color={theme.colors.secondary}>
                  <Eye size={32} />
                </IconWrapper>
                <CardTitle theme={theme}>Our Vision</CardTitle>
                <CardContent theme={theme}>{aboutData.vision}</CardContent>
              </InfoCard>
            )}

            {aboutData.values && (
              <InfoCard theme={theme}>
                <IconWrapper color={theme.colors.success}>
                  <Heart size={32} />
                </IconWrapper>
                <CardTitle theme={theme}>Our Values</CardTitle>
                <CardContent theme={theme}>{aboutData.values}</CardContent>
              </InfoCard>
            )}
          </Grid>
        </Section>
      )}

      {/* Services Section */}
      {aboutData.services && aboutData.services.length > 0 && (
        <Section>
          <SectionTitle theme={theme}>Our Services</SectionTitle>
          <Description theme={theme}>
            We provide comprehensive services to support and empower women human rights defenders
          </Description>
          
          <ServicesGrid>
            {aboutData.services.map((service, index) => (
              <ServiceCard key={index} theme={theme}>
                {service.icon && (
                  <ServiceIcon>{service.icon}</ServiceIcon>
                )}
                <ServiceTitle theme={theme}>{service.title}</ServiceTitle>
                <ServiceDescription theme={theme}>{service.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Section>
      )}

      {/* Objectives */}
      {aboutData.objectives && aboutData.objectives.length > 0 && (
        <Section style={{ background: theme.colors.background.secondary }}>
          <SectionTitle theme={theme}>Key Objectives</SectionTitle>
          
          <ObjectivesList>
            {aboutData.objectives.map((objective, index) => (
              <ObjectiveItem key={index} theme={theme}>
                <ObjectiveIcon theme={theme}>
                  <CheckCircle size={14} />
                </ObjectiveIcon>
                <ObjectiveText theme={theme}>{objective}</ObjectiveText>
              </ObjectiveItem>
            ))}
          </ObjectivesList>
        </Section>
      )}

      {/* Board Members */}
      {aboutData.board_members && aboutData.board_members.length > 0 && (
        <BoardSection>
          <SectionTitle theme={theme}>Our Board</SectionTitle>
          <Description theme={theme}>
            The Board of the Ethiopian Women Human Rights Defenders Network provides strategic guidance and oversight.
          </Description>

          <BoardGrid>
            {aboutData.board_members.map((member, index) => {
              const initials = (member.name || 'B')
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase();

              return (
                <BoardCard key={index} theme={theme}>
                  <BoardAvatar theme={theme}>
                    {member.photo ? (
                      <img
                        src={getStorageUrl(member.photo)}
                        alt={member.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      initials
                    )}
                  </BoardAvatar>
                  <BoardName theme={theme}>{member.name}</BoardName>
                  {member.position && (
                    <BoardPosition theme={theme}>{member.position}</BoardPosition>
                  )}
                  {member.bio && (
                    <BoardBio theme={theme}>{member.bio}</BoardBio>
                  )}
                </BoardCard>
              );
            })}
          </BoardGrid>
        </BoardSection>
      )}

      {/* History */}
      {aboutData.history && (
        <Section>
          <SectionTitle theme={theme}>Our History</SectionTitle>
          
          <HistorySection theme={theme}>
            <IconWrapper color={theme.colors.primary} style={{ marginBottom: '2rem' }}>
              <History size={32} />
            </IconWrapper>
            <HistoryText theme={theme}>{aboutData.history}</HistoryText>
          </HistorySection>
        </Section>
      )}

      {/* Call to Action */}
      <Section>
        <CTASection theme={theme}>
          <CTATitle theme={theme}>Want to Learn More?</CTATitle>
          <CTAText theme={theme}>
            Get in touch with us to learn more about our work and how you can get involved
          </CTAText>
          <Button 
            variant="primary" 
            size="large"
            onClick={() => window.location.href = '/contact'}
          >
            Contact Us
            <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Button>
        </CTASection>
      </Section>

      <PublicFooter theme={theme} />
    </AboutContainer>
  );
};

export default AboutPage;
